<script lang="ts">
  /**
   * Today's Now progress: a ring with "done/committed" in the middle, where
   * committed = active + completed today (open slots don't count).
   *
   * With refill-same-day on, finished tasks free their slot and the count keeps
   * shifting, so a one-line explanation replaces the ring. Nothing renders while
   * nothing is committed.
   */
  import type { NowState } from '../lib/types';

  const SIZES = { sm: 44, lg: 64 } as const;

  interface Props {
    nowState: Pick<NowState, 'items' | 'completedToday' | 'refillSameDay'>;
    size?: keyof typeof SIZES;
  }

  let { nowState, size = 'sm' }: Props = $props();

  let done = $derived(nowState.completedToday.length);
  let committed = $derived(nowState.items.length + done);

  let px = $derived(SIZES[size]);
  let stroke = $derived(size === 'lg' ? 6 : 4.5);
  let radius = $derived((px - stroke) / 2);
  let circumference = $derived(2 * Math.PI * radius);
  let dashOffset = $derived(circumference * (1 - done / Math.max(committed, 1)));
</script>

{#if committed > 0}
  {#if nowState.refillSameDay}
    <p class="refill-note">Refill is on: finished tasks free their slot right away.</p>
  {:else}
    <div
      class="done-ring {size}"
      style="width: {px}px; height: {px}px;"
      role="img"
      aria-label="{done} of {committed} done today"
    >
      <svg width={px} height={px} viewBox="0 0 {px} {px}" aria-hidden="true">
        <circle class="track" cx={px / 2} cy={px / 2} r={radius} stroke-width={stroke} />
        {#if done > 0}
          <circle
            class="fill"
            cx={px / 2}
            cy={px / 2}
            r={radius}
            stroke-width={stroke}
            stroke-dasharray={circumference}
            stroke-dashoffset={dashOffset}
            transform="rotate(-90 {px / 2} {px / 2})"
          />
        {/if}
      </svg>
      <span class="count" aria-hidden="true">{done}/{committed}</span>
    </div>
  {/if}
{/if}

<style>
  .done-ring {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .done-ring svg {
    position: absolute;
    inset: 0;
  }

  .track {
    fill: none;
    stroke: #e6e1d9;
  }

  .fill {
    fill: none;
    stroke: #22c55e;
    stroke-linecap: round;
    transition: stroke-dashoffset 300ms ease-out;
  }

  @media (prefers-reduced-motion: reduce) {
    .fill {
      transition: none;
    }
  }

  .count {
    position: relative;
    font-size: 0.8125rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #141210;
    letter-spacing: -0.01em;
  }

  .done-ring.lg .count {
    font-size: 1.0625rem;
  }

  .refill-note {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 500;
    color: #5a544d;
  }
</style>
